import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useStorage } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { STORAGE_KEY } from '../../constants';
import type { StorageVotes, Vote, VotesData } from '../../types';
import { ViewType } from '../../types';
import {
  useChangeVoteMutation,
  useUnvoteMutation,
  useVoteMutation,
  useVotesQuery
} from '../api';
import { changeVoteLocal, unvoteLocal, voteLocal } from '../local-data';
import { fillData } from '../utils';

type VoteContextProps = {
  viewType: ViewType;
  data: VotesData;
  isVoting: boolean;
  setViewType(viewType: ViewType): void;
  vote(id: Vote['id']): void;
  unvote(id: Vote['id']): void;
  changeVote(unvoteId: Vote['id'], voteId: Vote['id']): void;
};

const VoteContext = createContext<VoteContextProps | undefined>(undefined);

export function useVoteContext(): VoteContextProps {
  const context = useContext(VoteContext);

  if (!context) {
    throw new Error('useVoteContext must be used within an VoteProvider');
  }

  return context;
}

export function VoteProvider({ children }: PropsWithChildren) {
  const { editMode, widgetId, answers } = useSettings();
  const [storedVote, { set: setLocalStorageData, get: getLocalStorageData }] =
    useStorage<StorageVotes | undefined>(STORAGE_KEY, undefined);

  const [initialized, setInitialized] = useState(false);
  const [viewType, setViewType] = useState<VoteContextProps['viewType']>(
    ViewType.VoteView
  );

  const queryKey = ['votes', widgetId, answers.length];

  const { isLoading, data, setData } = useVotesQuery(queryKey, answers);
  const { isPending: isVoteLoading, mutate: voteMutation } =
    useVoteMutation(queryKey);
  const { isPending: isUnvoteLoading, mutate: unvoteMutation } =
    useUnvoteMutation(queryKey);
  const { isPending: isChangeVoteLoading, mutate: changeVoteMutation } =
    useChangeVoteMutation(queryKey);

  const isVoting =
    isLoading || isVoteLoading || isUnvoteLoading || isChangeVoteLoading;

  const filledData = useMemo(() => {
    return fillData({
      answers,
      data
    });
  }, [answers, data]);

  useEffect(() => {
    if (!initialized && !isVoting) {
      setInitialized(true);
    }
  }, [isVoting]);

  useEffect(() => {
    if (!storedVote) {
      setViewType(ViewType.VoteView);
    }
  }, [storedVote]);

  useEffect(() => {
    if (editMode || isVoting) {
      return;
    }

    const isStoredAnswerExisted =
      !!storedVote && data?.votes.find(({ id }) => id === storedVote.id);

    if (isStoredAnswerExisted) {
      setViewType(ViewType.ResultView);
    } else {
      setViewType(ViewType.VoteView);
    }
  }, [storedVote, isVoting]);

  const handleVote = async (id: Vote['id']) => {
    setViewType(ViewType.ResultView);

    if (editMode) {
      const newData = voteLocal({
        id,
        answers,
        data
      });

      setData(newData);

      return;
    }

    voteMutation(id, {
      onSuccess({ pid }) {
        setLocalStorageData({
          id,
          pid
        });
      }
    });
  };

  const handleUnvote = async (id: Vote['id']) => {
    setViewType(ViewType.VoteView);

    if (editMode) {
      const newData = unvoteLocal({
        id,
        answers,
        data
      });

      setData(newData);

      return;
    }

    const storedValue = getLocalStorageData();

    if (storedValue?.pid) {
      unvoteMutation(
        { id, pid: storedValue.pid },
        {
          onSuccess() {
            setLocalStorageData(undefined);
          }
        }
      );
    }

    return;
  };

  const handleChangeVote = async (unvoteId: string, voteId: string) => {
    if (editMode) {
      const newData = changeVoteLocal({
        unvoteId,
        voteId,
        answers,
        data
      });

      setData(newData);

      return;
    }

    const storedValue = getLocalStorageData();

    if (storedValue?.pid) {
      changeVoteMutation(
        {
          unvoteId,
          voteId,
          pid: storedValue.pid
        },
        {
          onSuccess({ pid }) {
            setLocalStorageData({
              id: voteId,
              pid
            });
          }
        }
      );
    }
  };

  return (
    <VoteContext.Provider
      value={{
        viewType,
        data: filledData,
        isVoting,
        setViewType,
        vote: handleVote,
        unvote: handleUnvote,
        changeVote: handleChangeVote
      }}
    >
      {isVoting && !initialized ? null : children}
    </VoteContext.Provider>
  );
}
